import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, Clock, Star, Zap, CheckCircle2, Sparkles } from 'lucide-react';
import { CTAButton } from './ui/CTAButton';
export function PricingSection() {
  return <section id="pricing" className="pt-12 pb-10 px-4 bg-sage-600 relative overflow-hidden" data-id="element-176">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-sage-400 rounded-full mix-blend-overlay filter blur-[80px] opacity-30" data-id="element-177"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-naranja-500 rounded-full mix-blend-overlay filter blur-[100px] opacity-10" data-id="element-178"></div>
      <div className="absolute inset-0 opacity-[0.05]" style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
      backgroundSize: '24px 24px'
    }} data-id="element-179"></div>

      <div className="max-w-3xl mx-auto relative z-10" data-id="element-180">
        {/* Headline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-8" data-id="element-181">
          <motion.span initial={{
          scale: 0.9,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1,
          type: 'spring'
        }} className="inline-flex items-center gap-1.5 bg-naranja-500 text-white font-bold px-4 py-1.5 rounded-full text-xs mb-4 uppercase tracking-wider shadow-lg" data-id="element-182">
            <Zap className="w-3.5 h-3.5" data-id="element-183" />
            Oferta por tiempo limitado
          </motion.span>
          <h2 className="text-2xl md:text-4xl font-black text-white mb-3 leading-tight" data-id="element-184">
            Recupera tu tiempo y la salud de tu familia{' '}
            <span className="text-naranja-300" data-id="element-185">hoy mismo</span>.
          </h2>
          <p className="text-base text-sage-100 max-w-xl mx-auto font-medium" data-id="element-186">
            Este sistema te ahorrará al menos{' '}
            <strong className="text-white" data-id="element-187">5 horas</strong> de estrés a la
            semana y mucho dinero en comida desperdiciada.
          </p>
        </motion.div>

        {/* Main card */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20
      }} className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-sage-300" data-id="element-188">
          {/* Green top banner */}
          <div className="bg-sage-700 py-3 px-6 text-center" data-id="element-189">
            <p className="text-white font-bold text-sm flex items-center justify-center gap-2" data-id="element-190">
              <Sparkles className="w-4 h-4 text-naranja-300" data-id="element-191" />
              Más de 2,500 mamás ya lo tienen — ¡No te quedes fuera!
              <Sparkles className="w-4 h-4 text-naranja-300" data-id="element-192" />
            </p>
          </div>

          <div className="p-6 md:p-10" data-id="element-193">
            {/* Persuasive phrase */}
            <p className="text-center text-charcoal-900 font-black text-lg md:text-xl mb-6 leading-snug" data-id="element-194">
              Recupera tus mañanas y la salud de tus hijos por menos de lo que
              cuesta una salida a comer pizza 🍕
            </p>

            {/* Product image */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.15
          }} className="flex justify-center mb-6" data-id="element-195">
              <div className="w-64 md:w-80 rounded-2xl overflow-hidden shadow-lg border-4 border-sage-100" data-id="element-196">
                <img src="https://cdn.magicpatterns.com/uploads/tRUkUJyStDeDnVbqZnrXkR/10.png" alt="Kit de Loncheras Saludables - Producto" className="w-full h-auto object-cover" data-id="element-197" />
              </div>
            </motion.div>

            {/* What's included mini-list */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6 max-w-md mx-auto" data-id="element-198">
              {['Guía Maestra PDF', 'Planificador de 30 Días', 'Lista de Compras Inteligente', 'Recetas de Emergencia', 'Guía de Snacks Saludables', 'Acceso de por vida'].map((item, i) => <div key={i} className="flex items-center gap-2 text-sm text-charcoal-700" data-id="element-199">
                  <CheckCircle2 className="w-4 h-4 text-sage-500 flex-shrink-0" data-id="element-200" />
                  <span className="font-medium" data-id="element-201">{item}</span>
                </div>)}
            </div>

            {/* Price block */}
            <div className="text-center mb-6 bg-sage-50 py-5 px-4 rounded-2xl border border-sage-200" data-id="element-202">
              <p className="text-sage-700 font-bold uppercase tracking-widest text-xs mb-2" data-id="element-203">
                Precio Especial de Lanzamiento
              </p>
              <div className="flex flex-col items-center gap-0" data-id="element-204">
                <span className="text-lg text-charcoal-400 line-through decoration-naranja-500 decoration-2 font-bold" data-id="element-205">
                  $29.00 USD
                </span>
                <motion.span initial={{
                scale: 0.9
              }} whileInView={{
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                type: 'spring',
                stiffness: 300,
                delay: 0.2
              }} className="text-5xl md:text-6xl font-black text-sage-700" data-id="element-206">
                  $6.99 <span className="text-xl font-bold" data-id="element-207">USD</span>
                </motion.span>
              </div>
              <p className="text-charcoal-500 text-xs mt-2 font-medium" data-id="element-208">
                Pago único · Acceso de por vida · Descarga inmediata
              </p>
            </div>

            {/* CTA */}
            <div className="max-w-md mx-auto mb-5" data-id="element-209">
              <CTAButton href="https://www.rutinaspracticas.com/cart/52626980077890:1" className="w-full py-5 text-xl md:text-2xl tracking-wide" data-id="element-210">
                SÍ QUIERO EL KIT AHORA
              </CTAButton>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-6" data-id="element-211">
              <div className="flex items-center gap-1.5 text-xs text-charcoal-600 font-medium bg-sage-50 py-2 px-3 rounded-full border border-sage-200" data-id="element-212">
                <Lock className="w-3.5 h-3.5 text-sage-600" data-id="element-213" />
                Pago 100% seguro
              </div>
              <div className="flex items-center gap-1.5 text-xs text-charcoal-600 font-medium bg-sage-50 py-2 px-3 rounded-full border border-sage-200" data-id="element-214">
                <Zap className="w-3.5 h-3.5 text-naranja-500" data-id="element-215" />
                Acceso inmediato
              </div>
              <div className="flex items-center gap-1.5 text-xs text-charcoal-600 font-medium bg-sage-50 py-2 px-3 rounded-full border border-sage-200" data-id="element-216">
                <Star className="w-3.5 h-3.5 text-mostaza-500" data-id="element-217" />
                +2,500 mamás felices
              </div>
            </div>

            {/* Guarantee */}
            <div className="bg-sage-100 p-4 rounded-2xl border border-sage-300 flex items-start gap-3 max-w-lg mx-auto" data-id="element-218">
              <ShieldCheck className="w-9 h-9 text-sage-600 flex-shrink-0 mt-0.5" data-id="element-219" />
              <div data-id="element-220">
                <h4 className="font-bold text-sm text-charcoal-900 mb-1" data-id="element-221">
                  Garantía de Satisfacción de 7 Días
                </h4>
                <p className="text-charcoal-600 text-xs leading-relaxed" data-id="element-222">
                  Si en 7 días sientes que tus mañanas no han mejorado, te
                  devolvemos tu dinero sin preguntas. Sin riesgo para ti.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}